<template>
  <div class="promo-interaction-opt-wrap">
    <div class="interaction-label">User</div>
    <v-select
      v-model="matchTypeModel"
      variant="outlined"
      density="compact"
      hideDetails
      class="min-w-28"
      placeholder="Match Type"
      :disabled="disabled"
      :items="matchTypeItems"
      itemTitle="text"
      :menuProps="{ maxHeight: 310, offsetY: true }"
      height="32px"
      bgColor="#ffffff"
      @update:modelValue="updateFilter"
    />
    <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
    <v-select
      v-if="hasMatchType"
      v-model="interactionTypeModel"
      chips
      multiple
      variant="outlined"
      density="compact"
      hideDetails
      :disabled="disabled"
      itemTitle="text"
      :items="interactionTypeItems"
      placeholder="Event"
      :menuProps="{ maxHeight: 310, offsetY: true, minWidth: 240 }"
      class="pr-interaction-checklist"
      bgColor="#ffffff"
      @update:modelValue="updateFilter"
    />
    <v-icon v-if="interactionTypeModel">md:chevron_right</v-icon>
    <v-select
      v-if="interactionTypeModel"
      v-model="operatorModel"
      variant="outlined"
      density="compact"
      hideDetails
      :disabled="disabled"
      itemTitle="text"
      :items="PromoOperatorItems"
      placeholder="Operator"
      :menuProps="{ maxHeight: 310, offsetY: true }"
      class="min-w-20"
      bgColor="#ffffff"
      @update:modelValue="updateFilter"
    />
    <v-icon>md:chevron_right</v-icon>
    <v-autocomplete
      v-if="shouldShowPromoSelection"
      v-model="pathSlugs"
      :items="
        allPromptsStore.getPromptsLoading
          ? Array.from({ length: 5 })
          : allPromptsStore.sortedAllPrompts
      "
      itemTitle="name"
      itemValue="id"
      :disabled="disabled"
      placeholder="Select configuration"
      bgColor="#ffffff"
      hideDetails
      chips
      multiple
      variant="outlined"
      density="compact"
      class="segment-builder--subtypes _subtypes min-w-42"
      :menuProps="{ maxHeight: 310, offsetY: true, minWidth: 240, maxWidth: 520 }"
      @update:modelValue="updateFilter"
    >
      <template #item="{ props }">
        <v-list-item v-bind="props">
          <RfBaseSkeleton
            v-if="allPromptsStore.getPromptsLoading"
            width="240"
            height="24"
            class="my-1 !w-full"
          />
          <template v-if="!allPromptsStore.getPromptsLoading" #prepend="{ isSelected }">
            <v-checkbox hideDetails class="ml-n2 mt-0 pt-0" readonly :modelValue="isSelected" />
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import {
  InteractionTypeItems,
  MatchTypeItemsAlter,
  PromoOperators,
} from "@/utils/constants/SegmentsConstants";
import RfChevronIcon from "../icons/RfChevronIcon.vue";

export default {
  name: "RfPromoInteractions",
  components: { RfBaseSkeleton, RfChevronIcon },
  props: ["filter", "disabled", "isInOrGroups", "currentPromoOperator"],
  setup: () => ({
    allPromptsStore: useAllPromptsStore(),
    matchTypeItems: Object.values(MatchTypeItemsAlter),
    interactionTypeItems: Object.values(InteractionTypeItems),
    PromoOperatorItems: Object.values(PromoOperators),
  }),
  data() {
    return {
      matchTypeModel: null,
      interactionTypeModel: null,
      operatorModel: null,
      pathSlugs: [],
    };
  },
  computed: {
    hasMatchType() {
      return this.matchTypeModel !== null;
    },
    shouldShowPromoSelection() {
      return !!this.operatorModel;
    },
  },
  watch: {
    currentPromoOperator(newOperator) {
      if (newOperator && this.operatorModel !== newOperator) {
        this.operatorModel = newOperator;
        this.updateFilter();
      }
    },
    operatorModel(newOperator) {
      this.$emit("operator-changed", newOperator);
      this.updateFilter();
    },
  },
  mounted() {
    this.loadPromos();
    this.prepareFilter();
  },
  methods: {
    updateFilter() {
      const promos = this.pathSlugs.reduce(
        (obj, promo_slug) => ({
          ...obj,
          [promo_slug]: {
            promo_slug,
            positive_match: this.matchTypeModel,
            interaction_types: this.interactionTypeModel,
          },
        }),
        {},
      );
      this.$emit("update:model", {
        promos,
        options: { operator: this.operatorModel || PromoOperators.AND.value },
      });
    },
    prepareFilter() {
      const { promos, options } = this.filter;
      this.pathSlugs = Object.keys(promos);
      const firstValue = Object.values(promos)[0];
      this.matchTypeModel = firstValue?.positive_match;
      this.interactionTypeModel = firstValue?.interaction_types;

      // Use the currentPromoOperator prop if available (for consistency with existing instances),
      // otherwise fall back to options.operator, and finally default to AND
      this.operatorModel =
        this.currentPromoOperator || options?.operator || PromoOperators.AND.value;
    },
    loadPromos() {
      if (this.allPromptsStore.prompts.length) return;
      this.allPromptsStore.getPrompts(this.$route.params.aid, { nonBlocking: true });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-autocomplete) .v-select__selections > .v-chip {
  margin: 2px 4px;

  ~ input {
    min-width: 20px;
    max-width: 20px;
  }
}
</style>
